/**
 * The canonical team registry — one team identity for the whole app.
 *
 * A team is never its display name. Per-module alias maps are how a club silently vanishes from
 * the league table: every provider brings another naming convention, and divergent maps drift.
 *
 * `id` is the key — a hand-written slug, never derived from a display name and never from a
 * provider id, so renaming a club is a one-line display change that repoints nothing. `aliases` is
 * the union of every spelling that has ever meant this club (ours, historic, and the provider's),
 * so `findTeamId` can accept anything and still land on one row.
 *
 * `id` is branded so the compiler, not grep, finds the call sites that still pass a display name.
 *
 * Provider ids are deliberately NOT in this file: they live in the `team_provider_refs` table,
 * keyed per provider, so switching providers throws away ref rows without touching team identity.
 */

/** Our own stable team key. Branded so a display name can't be passed where an id is expected. */
export type TeamId = string & { readonly __brand: 'TeamId' }

export interface TeamRecord {
  /** Our own stable slug. Never changes, never derived from a provider or a display name. */
  id: TeamId
  /** The app's canonical display name. */
  name: string
  /** Three-letter code — matches src/assets/club-shields/<abbr>.svg and docs/shields/. */
  abbr: string
  /** Display-only short form, set only where the canonical name reads badly in a UI row. */
  shortName?: string
  /**
   * Tighter still, and **only** for the score picker's two-club heading row, where half a
   * phone's width wraps the name onto a second line. Set it where that happens, nowhere else.
   * The form: initial the first word, keep the second whole ("Aston Villa" → "A. Villa").
   */
  pickerName?: string
  /** Competing this season. Relegated clubs stay rows so their supporters keep a club league. */
  isActive: boolean
  /**
   * Every other spelling that must resolve to this club: our own historic variants and the
   * provider's canonical name. Exact matches only after normalisation — there is deliberately no
   * fuzzy matcher (a wrong auto-match is much worse than a loud failure).
   */
  aliases: readonly string[]
}

function id(value: string): TeamId {
  return value as TeamId
}

export const TEAMS: readonly TeamRecord[] = [
  { id: id('arsenal'), name: 'Arsenal', abbr: 'ARS', isActive: true, aliases: ['Arsenal FC'] },
  {
    id: id('aston-villa'),
    name: 'Aston Villa',
    abbr: 'AVL',
    pickerName: 'A. Villa',
    isActive: true,
    aliases: ['Villa'],
  },
  {
    id: id('bournemouth'),
    name: 'Bournemouth',
    abbr: 'BOU',
    isActive: true,
    aliases: ['AFC Bournemouth'],
  },
  { id: id('brentford'), name: 'Brentford', abbr: 'BRE', isActive: true, aliases: [] },
  {
    id: id('brighton'),
    name: 'Brighton',
    abbr: 'BHA',
    isActive: true,
    aliases: ['Brighton & Hove Albion', 'Brighton and Hove Albion'],
  },
  { id: id('chelsea'), name: 'Chelsea', abbr: 'CHE', isActive: true, aliases: [] },
  {
    id: id('coventry'),
    name: 'Coventry City',
    abbr: 'COV',
    shortName: 'Coventry',
    isActive: true,
    aliases: [],
  },
  {
    id: id('crystal-palace'),
    name: 'Crystal Palace',
    abbr: 'CRY',
    pickerName: 'C. Palace',
    isActive: true,
    aliases: [],
  },
  { id: id('everton'), name: 'Everton', abbr: 'EVE', isActive: true, aliases: [] },
  { id: id('fulham'), name: 'Fulham', abbr: 'FUL', isActive: true, aliases: [] },
  {
    id: id('hull'),
    name: 'Hull City',
    abbr: 'HUL',
    shortName: 'Hull',
    isActive: true,
    aliases: [],
  },
  {
    id: id('ipswich'),
    name: 'Ipswich Town',
    abbr: 'IPS',
    shortName: 'Ipswich',
    isActive: true,
    aliases: [],
  },
  { id: id('leeds'), name: 'Leeds', abbr: 'LEE', isActive: true, aliases: ['Leeds United'] },
  { id: id('liverpool'), name: 'Liverpool', abbr: 'LIV', isActive: true, aliases: [] },
  {
    id: id('man-city'),
    name: 'Manchester City',
    abbr: 'MCI',
    shortName: 'Man City',
    isActive: true,
    aliases: [],
  },
  {
    id: id('man-utd'),
    name: 'Man Utd',
    abbr: 'MUN',
    isActive: true,
    aliases: ['Manchester United', 'Man United'],
  },
  {
    id: id('newcastle'),
    name: 'Newcastle',
    abbr: 'NEW',
    isActive: true,
    aliases: ['Newcastle United'],
  },
  {
    id: id('nottingham-forest'),
    name: 'Nottingham Forest',
    abbr: 'NFO',
    shortName: 'Nottm Forest',
    pickerName: 'N. Forest',
    isActive: true,
    aliases: ["Nott'm Forest"],
  },
  { id: id('sunderland'), name: 'Sunderland', abbr: 'SUN', isActive: true, aliases: [] },
  {
    id: id('tottenham'),
    name: 'Tottenham',
    abbr: 'TOT',
    isActive: true,
    aliases: ['Tottenham Hotspur', 'Spurs'],
  },
  // Relegated but still supportable — no fixtures, no PL table row, but a club league.
  {
    id: id('leicester'),
    name: 'Leicester City',
    abbr: 'LEI',
    shortName: 'Leicester',
    isActive: false,
    aliases: [],
  },
  {
    id: id('west-ham'),
    name: 'West Ham United',
    abbr: 'WHU',
    shortName: 'West Ham',
    isActive: false,
    aliases: ['West Ham'],
  },
]

/** Loosens spacing, case, punctuation and the `&`/`and` split so lookups aren't brittle. */
function normalizeKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

const BY_ID = new Map<string, TeamRecord>(TEAMS.map((team) => [team.id, team]))

const BY_KEY = ((): Map<string, TeamRecord> => {
  const map = new Map<string, TeamRecord>()
  const add = (key: string, team: TeamRecord) => {
    const normalized = normalizeKey(key)
    const existing = map.get(normalized)
    if (existing && existing.id !== team.id) {
      // Two clubs claiming one spelling is the exact ambiguity this registry exists to remove;
      // fail at module load rather than resolving it arbitrarily at runtime.
      throw new Error(
        `Team registry: "${key}" maps to both ${existing.id} and ${team.id} — aliases must be unique`
      )
    }
    map.set(normalized, team)
  }
  for (const team of TEAMS) {
    add(team.id, team)
    add(team.name, team)
    add(team.abbr, team)
    if (team.shortName) add(team.shortName, team)
    for (const alias of team.aliases) add(alias, team)
  }
  return map
})()

/** Clubs **competing this season** — fixtures, the quest pickers, the PL table. */
export const activeTeams: readonly TeamRecord[] = TEAMS.filter((team) => team.isActive)

/** Clubs that are supportable but not competing. */
export const inactiveTeams: readonly TeamRecord[] = TEAMS.filter((team) => !team.isActive)

/** Every club a user can support, ordered by how they read in the picker. */
export const supportableTeams: readonly TeamRecord[] = [...TEAMS].sort((a, b) =>
  teamDisplayName(a.id).localeCompare(teamDisplayName(b.id))
)

export function teamById(value: TeamId | string): TeamRecord | undefined {
  return BY_ID.get(value)
}

/** True for a string that is already one of our team ids. */
export function isTeamId(value: string): value is TeamId {
  return BY_ID.has(value)
}

/**
 * Resolves an id, a canonical name, a short name, an abbreviation or any historic/provider alias
 * to our team id. Returns null on anything unknown — callers decide whether that's fatal.
 */
export function findTeamId(value: string | null | undefined): TeamId | null {
  if (!value) return null
  return BY_KEY.get(normalizeKey(value))?.id ?? null
}

/** `findTeamId` for call sites where an unknown team is a bug, not a possibility. */
export function teamId(value: string): TeamId {
  const resolved = findTeamId(value)
  if (resolved === null) {
    throw new Error(`Unknown team: ${JSON.stringify(value)}`)
  }
  return resolved
}

/** The club's canonical name. Prefer `teamDisplayName` for anything user-facing. */
export function teamName(value: TeamId | string): string {
  return teamById(value)?.name ?? String(value)
}

/**
 * What we call a club in UI copy — the short form where one exists, the canonical name otherwise.
 * Every user-facing render goes through here rather than reaching for `name`/`shortName` directly.
 */
export function teamDisplayName(value: TeamId | string): string {
  const team = teamById(value)
  if (!team) return String(value)
  return team.shortName ?? team.name
}

/**
 * The score picker only. It sits both clubs in one row of half-width columns, and three
 * names wrap there; this is the display name with those three tightened. Every other club, and any
 * unknown id, comes back exactly as `teamDisplayName` would render it.
 *
 * Not for spoken labels — an initial reads worse than it looks (ScorePicker's aria-labels stay on
 * `teamDisplayName`).
 */
export function teamPickerName(value: TeamId | string): string {
  return teamById(value)?.pickerName ?? teamDisplayName(value)
}

export function teamAbbr(value: TeamId | string): string {
  const team = teamById(value)
  if (team) return team.abbr
  // Unknown ids shouldn't reach the UI, but a three-letter fallback beats rendering a raw slug.
  return String(value).slice(0, 3).toUpperCase()
}
